/* ===================================================================
   TourController -- the interactive product tour, mounted in AppShell so it
   can walk the user across screens.

   Faithful port of the prototype's coach-mark tour
   ("Rhody Reviews.dc.html" bubble 108-136, step data support ~3558-3597):
   a dark bubble pinned bottom-center with a progress bar, step label, title,
   numbered sub-steps, a green "Why it matters" strip, and Back / Next /
   Finish controls. Each step highlights the LIVE element it points at.

   HOW IT HOOKS THE SHELL:
     - Tutorials.tsx dispatches window CustomEvent `rhody:start-tour` with
       `detail:{ step }`. `step` is the card number (1-6) for a card's
       "Show me how", or undefined for the header "Take the tour" (start at
       step 1). This controller LISTENS for that event and drives the tour.
     - Each step names a `target` that matches a `data-tour="..."` hook already
       placed on the shell nav / screens (reviews|customers|template|automate|
       google|theme|request|tutorials|widget|reports). On each step we:
         1. navigate() to the step's route so the target is on-screen, then
         2. set `data-tour-active` on <body> and `data-tour-lit="1"` on the
            matching element -> the CSS ring animation in overlays.css lights
            it up. Only ONE element is lit at a time.
     - Finishing or skipping clears the highlight and returns to /tutorials.

   The bubble is intentionally bottom-center (not pixel-anchored to each
   target) exactly like the prototype -- the ring draws the eye to the live
   element while the bubble explains it. This keeps the tour robust across
   responsive layouts without brittle coordinate math.

   Tick sounds: OPTIONAL per the brief. A lightweight WebAudio blip is wired
   behind a mute toggle; it never throws if audio is unavailable.
   =================================================================== */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../styles/overlays.css';

/* The six tour steps, VERBATIM from the prototype's step data. `target` is the
   data-tour key on the live element; `route` is where that element lives. */
interface TourStep {
  key: string;
  target: string;
  route: string;
  title: string;
  text: string;
  sub: string[];
  why: string;
}

const STEPS: TourStep[] = [
  {
    key: 'reviews',
    target: 'reviews',
    route: '/reviews',
    title: 'This is your daily job',
    text: 'Every day, one thing matters most: replying to new Google reviews. The red badge on Reviews shows how many are waiting.',
    sub: [
      'Look for the number badge next to "Reviews"',
      'That count = replies waiting for your approval',
      'Click Reviews any time to work through them',
    ],
    why: 'Businesses that reply to reviews rank higher and win more trust.',
  },
  {
    key: 'request',
    target: 'request',
    route: '/dashboard',
    title: 'Ask for a review in seconds',
    text: 'The blue Request reviews button is your money-maker. Tap it right after a job to send a customer your Google review link.',
    sub: [
      'Click "Request reviews" (top-right)',
      'Pick a customer or type a name + mobile number',
      'Rhody sends an SMS or email with your review link',
    ],
    why: 'More requests then more reviews. This is the single biggest lever you have.',
  },
  {
    key: 'customers',
    target: 'customers',
    route: '/customers',
    title: 'Build your request list',
    text: "Customers is who Rhody asks. Import once and you're set -- the list powers both one-off requests and automations.",
    sub: [
      'Open Customers from the sidebar',
      'Import a CSV or add people by hand',
      'Mark anyone "do-not-contact" to skip them forever',
    ],
    why: 'A clean customer list means requests reach real people at the right number.',
  },
  {
    key: 'template',
    target: 'template',
    route: '/templates',
    title: 'Set the words you send',
    text: "Templates are the messages that go out. They're grouped by moment -- after-job, reminders, seasonal -- and one is your default.",
    sub: [
      'Open Templates',
      'Pick the "After a completed job" template',
      'Set it as default -- Rhody uses it automatically',
    ],
    why: 'The right message at the right moment gets far more replies.',
  },
  {
    key: 'automate',
    target: 'automate',
    route: '/automations',
    title: 'Put it on autopilot',
    text: 'Automations ask for reviews after every job so you never have to remember. You still approve every reply -- nothing posts on its own.',
    sub: [
      'Open Automations',
      'Turn on the master switch',
      'Adjust the after-job timing in plain English',
      'Requests respect quiet hours & do-not-contact',
    ],
    why: "Set it once and reviews keep coming in while you're on the tools.",
  },
  /* The 'theme' step ("Read it anywhere" -- switch Light / Dark / Outdoor) is
     RETIRED (Steve, 2026-08-06). The appearance switcher is gone; the app is
     outdoor high-contrast always, so there is nothing to teach and the step's
     target ([data-tour="theme"]) no longer exists. */
];

/* POST-CHUTE guided tour (2026-07-09). After the linear chute lands the owner
   on the dashboard (Welcome step 4 dispatches 'rhody:start-setup-tour'), this
   walks the REMAINDER of setup with the same coach-mark mechanism: brand voice
   (pre-assumed, tweak it), the review widget, the reactivation play, and
   importing real customers + the first real send (which leads to the card
   gate). These are NOT chute steps -- the chute already got them to first
   value; this finishes setup so they do not abandon. */
const SETUP_STEPS: TourStep[] = [
  {
    key: 'setup-voice',
    target: 'profile',
    route: '/settings',
    title: 'Make it sound like you',
    text: 'We already picked a friendly brand voice so nothing is blank. Open Settings to tweak the tone and sign-off so every AI-drafted reply sounds like your business.',
    sub: [
      'Open Settings (bottom of the sidebar)',
      'Find Brand voice',
      'Adjust the tone and sign-off -- you approve every reply before it posts',
    ],
    why: 'Replies in your own voice build more trust and never read as robotic.',
  },
  {
    key: 'setup-widget',
    target: 'widget',
    route: '/widget',
    title: 'Show off your reviews',
    text: 'Your review widget puts your best reviews and star rating right on your website. Copy one snippet, paste it once, and it updates itself.',
    sub: [
      'Open Site Widget',
      'Copy the embed snippet',
      'Paste it into your website once',
    ],
    why: 'Fresh reviews on your own site turn visitors into customers.',
  },
  {
    key: 'setup-reactivation',
    target: 'customers',
    route: '/customers',
    title: 'Ask the customers you already earned',
    text: 'Reactivation asks your PAST customers for a review. It is the fastest way to get real reviews in your first week -- the work is already done.',
    sub: [
      'Open Customers',
      'Start a reactivation ask to past customers',
      'Every eligible customer gets the same public review link',
    ],
    why: 'You already did great work. Reactivation turns it into reviews now.',
  },
  {
    key: 'setup-import',
    target: 'request',
    route: '/dashboard',
    title: 'Bring in customers and send for real',
    text: 'Import your customer list (or connect your scheduling tool), then send your first real review request. This is where you start your free trial with a card.',
    sub: [
      'Import customers or connect your tool',
      'Tap Request reviews',
      'Add a card to start your 14-day free trial and send',
    ],
    why: 'Your card is only needed for real customer sends -- never before.',
  },
];

/* Light the live element for `target` (and clear any previous). Returns a
   cleanup that removes the highlight. Runs after a short delay so the just-
   navigated route has painted its data-tour element. */
function lightTarget(target: string): () => void {
  let raf = 0;
  const timer = window.setTimeout(() => {
    raf = window.requestAnimationFrame(() => {
      document.body.setAttribute('data-tour-active', '1');
      document
        .querySelectorAll('[data-tour-lit]')
        .forEach((el) => el.removeAttribute('data-tour-lit'));
      const el = document.querySelector(`[data-tour="${target}"]`);
      if (el) {
        el.setAttribute('data-tour-lit', '1');
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    });
  }, 120);
  return () => {
    window.clearTimeout(timer);
    if (raf) window.cancelAnimationFrame(raf);
  };
}

function clearHighlight() {
  document.body.removeAttribute('data-tour-active');
  document
    .querySelectorAll('[data-tour-lit]')
    .forEach((el) => el.removeAttribute('data-tour-lit'));
}

export function TourController() {
  const navigate = useNavigate();
  // null = tour not running; otherwise the 0-based step index.
  const [step, setStep] = useState<number | null>(null);
  // Which tour is running: the default product tour (STEPS) or the post-chute
  // setup tour (SETUP_STEPS). Both use the identical coach-mark mechanism.
  const [steps, setSteps] = useState<TourStep[]>(STEPS);
  const [muted, setMuted] = useState(false);
  const audioRef = useRef<AudioContext | null>(null);

  const active = step != null && step >= 0 && step < steps.length;

  // A short tick blip (optional). Never throws if WebAudio is unavailable.
  const tick = useCallback(
    (kind: 'step' | 'done' = 'step') => {
      if (muted) return;
      try {
        const AC =
          window.AudioContext ||
          (window as unknown as { webkitAudioContext?: typeof AudioContext })
            .webkitAudioContext;
        if (!AC) return;
        const ctx = audioRef.current ?? (audioRef.current = new AC());
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = kind === 'done' ? 880 : 660;
        gain.gain.value = 0.04;
        osc.connect(gain).connect(ctx.destination);
        const t = ctx.currentTime;
        gain.gain.setValueAtTime(0.04, t);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.12);
        osc.start(t);
        osc.stop(t + 0.13);
      } catch {
        /* audio unavailable -- tour continues silently */
      }
    },
    [muted],
  );

  // Listen for the Tutorials-dispatched start event. detail.step is the card
  // number (1-6) or undefined ("Take the tour" -> start at step 1 / index 0).
  useEffect(() => {
    const onStart = (e: Event) => {
      const detail = (e as CustomEvent<{ step?: number }>).detail;
      const n = detail?.step;
      const idx = typeof n === 'number' && n >= 1 && n <= STEPS.length ? n - 1 : 0;
      setSteps(STEPS);
      setStep(idx);
      tick();
    };
    // POST-CHUTE setup tour (Welcome step 4). Starts at step 1 of SETUP_STEPS.
    const onStartSetup = () => {
      setSteps(SETUP_STEPS);
      setStep(0);
      tick();
    };
    window.addEventListener('rhody:start-tour', onStart as EventListener);
    window.addEventListener('rhody:start-setup-tour', onStartSetup as EventListener);
    return () => {
      window.removeEventListener('rhody:start-tour', onStart as EventListener);
      window.removeEventListener('rhody:start-setup-tour', onStartSetup as EventListener);
    };
  }, [tick]);

  // Drive navigation + highlight whenever the active step changes.
  useEffect(() => {
    if (!active) {
      clearHighlight();
      return;
    }
    const cur = steps[step as number];
    navigate(cur.route);
    const cleanup = lightTarget(cur.target);
    return cleanup;
  }, [active, step, steps, navigate]);

  // Clear the highlight if the controller unmounts mid-tour.
  useEffect(() => () => clearHighlight(), []);

  if (!active) return null;

  const idx = step as number;
  const cur = steps[idx];
  const isFirst = idx === 0;
  const isLast = idx === steps.length - 1;
  const pct = Math.round(((idx + 1) / steps.length) * 100);

  function end() {
    clearHighlight();
    const wasSetup = steps === SETUP_STEPS;
    setStep(null);
    // The post-chute setup tour finishes INTO the product (dashboard); the
    // on-demand product tour returns to the Tutorials hub it launched from.
    navigate(wasSetup ? '/dashboard' : '/tutorials');
  }
  function next() {
    if (isLast) {
      tick('done');
      end();
      return;
    }
    tick();
    setStep(idx + 1);
  }
  function back() {
    if (isFirst) return;
    tick();
    setStep(idx - 1);
  }

  return (
    <div className="rh-tour" role="dialog" aria-label="Product tour">
      <div className="rh-tour__card">
        <div className="rh-tour__progress">
          <div style={{ width: `${pct}%` }} />
        </div>
        <div className="rh-tour__inner">
          <div className="rh-tour__top">
            <span className="rh-tour__stepnum">
              Step {idx + 1} of {steps.length}
            </span>
            <div className="rh-tour__topbtns">
              <button
                className="rh-tour__sound"
                title={muted ? 'Unmute' : 'Mute'}
                aria-label={muted ? 'Unmute tour sounds' : 'Mute tour sounds'}
                onClick={() => setMuted((m) => !m)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path d="M4 9v6h4l5 4V5L8 9H4z" />
                  {muted ? (
                    <path d="M17 9l4 4M21 9l-4 4" />
                  ) : (
                    <path d="M16.5 8.5a5 5 0 0 1 0 7" />
                  )}
                </svg>
              </button>
              <button className="rh-tour__skip" onClick={end}>
                Skip tour
              </button>
            </div>
          </div>

          <div className="rh-tour__title">{cur.title}</div>
          <div className="rh-tour__text">{cur.text}</div>

          <div className="rh-tour__sub">
            {cur.sub.map((s, i) => (
              <div className="rh-tour__subrow" key={i}>
                <div className="rh-tour__subnum">{i + 1}</div>
                <div className="rh-tour__subtext">{s}</div>
              </div>
            ))}
          </div>

          <div className="rh-tour__why">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5bd07f" strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }}>
              <path d="M12 2l2.9 6.3 6.9.7-5.2 4.6 1.5 6.8L12 17.6 5.9 20.4l1.5-6.8L2.2 9l6.9-.7z" />
            </svg>
            <div className="rh-tour__why-text">
              <strong>Why it matters:</strong> {cur.why}
            </div>
          </div>

          <div className="rh-tour__nav">
            {!isFirst && (
              <button className="rh-tour__back" onClick={back}>
                Back
              </button>
            )}
            <div className="rh-tour__spacer" />
            {isLast ? (
              <button className="rh-tour__finish" onClick={next}>
                Finish
              </button>
            ) : (
              <button className="rh-tour__next" onClick={next}>
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
